import { type FC, lazy, Suspense, useContext, useState } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { Link } from 'dibs-elements/exports/Link.tsx';
import CirclePlus from 'dibs-icons/exports/legacy/CirclePlus.ts';
import CheckmarkCircle from 'dibs-icons/exports/legacy/CheckmarkCircle.ts';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight.ts';
import SellerFormContext from './SellerFormContext.tsx';
import { trackSellerApplicationEvent } from '../helpers/sellerApplicationTracking.ts';

import styles from './styles/SellerApplicationFormInventoryShared.scss';

import { type SellerApplicationFormInventorySample_viewer$key } from './__generated__/SellerApplicationFormInventorySample_viewer.graphql.ts';

const SellerApplicationFormInventorySampleModalLazy = lazy(
    () =>
        import(
            /* webpackChunkName: "SellerApplicationFormInventorySampleModal" */ './SellerApplicationFormInventorySampleModal.tsx'
        )
);

const viewerFragment = graphql`
    fragment SellerApplicationFormInventorySample_viewer on Viewer {
        ...SellerApplicationFormInventorySampleModal_viewer
    }
`;

type Props = {
    index: number;
    disabled: boolean;
    viewer: SellerApplicationFormInventorySample_viewer$key;
};

export const SellerApplicationFormInventorySample: FC<Props> = ({
    index,
    disabled,
    viewer: viewerRef,
}) => {
    const { state } = useContext(SellerFormContext);
    const viewer = useFragment(viewerFragment, viewerRef);

    const [isOpen, setIsOpen] = useState(false);

    const { title } = state.formDataBusinessInventory.items?.[index] || {};

    const handleOpenModal = (): void => {
        if (!disabled) {
            setIsOpen(true);
            trackSellerApplicationEvent({
                type: 'professional',
                action: 'add item',
                interactionName: 'add_item',
                stepInteractionType: `click on Add Item ${index + 1}`,
            });
        }
    };

    return (
        <div data-tn="application-form-inventory-sample">
            <Link
                className={classnames(styles.itemLink, {
                    [styles.disabledItemLink]: !!disabled,
                })}
                onClick={handleOpenModal}
            >
                <div
                    className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.sassyFontBody)}
                >
                    {title ? (
                        <>
                            <CheckmarkCircle className={styles.icon} />
                            {title}
                        </>
                    ) : (
                        <>
                            <CirclePlus className={styles.icon} />
                            <FormattedMessage
                                id="SellerApplicationForm.addItemLabel"
                                defaultMessage="Add Item {itemNum}"
                                values={{ itemNum: index + 1 }}
                            />
                        </>
                    )}
                </div>
                <ArrowRight className={styles.icon} />
            </Link>
            {isOpen && (
                <Suspense fallback={null}>
                    <SellerApplicationFormInventorySampleModalLazy
                        viewer={viewer}
                        isOpen={isOpen}
                        index={index}
                        setIsOpen={setIsOpen}
                    />
                </Suspense>
            )}
        </div>
    );
};
